"use client";

import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatTime12h } from "@convex/lib/reservationTime";

interface ReservationAvailabilityPickerProps {
  date: string; // YYYY-MM-DD
  partySize: number;
  onTimeSelected: (time: string) => void;
  selectedTime?: string;
  locationId?: string;
}

const timeSlotBaseClass =
  "min-h-11 rounded-lg border px-1.5 text-xs font-semibold leading-tight text-[var(--roma-espresso)] transition-colors sm:min-h-0 sm:h-10 sm:px-3 sm:text-sm";

export function ReservationAvailabilityPicker({
  date,
  partySize,
  onTimeSelected,
  selectedTime,
  locationId,
}: ReservationAvailabilityPickerProps) {
  const result = useQuery(api.reservationAvailability.getSlots, { date, partySize, locationId });

  if (result === undefined) {
    // Slot-shaped skeleton: same grid geometry and cell height as the real
    // grid so the layout doesn't jump when times arrive.
    return (
      <div className="space-y-1.5 sm:space-y-3" aria-busy="true">
        <p role="status" className="text-xs font-medium text-[var(--roma-espresso)]/76 sm:text-sm">
          Checking available times for your party…
        </p>
        <div
          aria-hidden="true"
          className="h-3.5 w-20 animate-pulse rounded bg-[var(--roma-linen)]/60 motion-reduce:animate-none sm:h-4 sm:w-24"
        />
        <div
          aria-hidden="true"
          className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2"
        >
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                timeSlotBaseClass,
                "animate-pulse border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/40 motion-reduce:animate-none",
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  if (result.closed) {
    return (
      <div className="rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-4 text-center sm:p-6">
        <p className="text-sm font-medium text-[var(--roma-espresso)]">
          Restaurant is closed on this day
        </p>
        <p className="mt-1 text-xs text-[var(--roma-espresso)]/72">
          Please select a different date.
        </p>
      </div>
    );
  }

  if ("error" in result && result.error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center sm:p-6">
        <p className="text-sm font-medium text-destructive">{result.error}</p>
      </div>
    );
  }

  if (result.slots.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-4 text-center sm:p-6">
        <p className="text-sm font-medium text-[var(--roma-espresso)]">
          No available times for this date
        </p>
        <p className="mt-1 text-xs text-[var(--roma-espresso)]/72">
          Try a different date, choose a smaller party size, or call us and we can check manually.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 sm:space-y-3">
      <p className="text-xs font-medium text-[var(--roma-espresso)]/80 sm:text-sm">
        Select a time:
      </p>
      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2">
        {result.slots.map((slot) => {
          const isSelected = selectedTime === slot.time;
          const isDisabled = !slot.available;

          return (
            <Button
              key={slot.time}
              type="button"
              variant="ghost"
              size="sm"
              disabled={isDisabled}
              onClick={() => onTimeSelected(slot.time)}
              className={cn(
                timeSlotBaseClass,
                "border shadow-none hover:text-[var(--roma-espresso)]",
                isDisabled && "cursor-not-allowed border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/25 text-[var(--roma-espresso)]/35 opacity-100",
                !isDisabled &&
                  !isSelected &&
                  slot.capacity === "high" &&
                  "border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] hover:border-[var(--roma-olive)]/55 hover:bg-[var(--roma-linen)]/50",
                !isDisabled &&
                  !isSelected &&
                  slot.capacity === "medium" &&
                  "border-amber-600/60 bg-amber-600/8 hover:border-amber-700/70 hover:bg-amber-600/12",
                !isDisabled &&
                  !isSelected &&
                  slot.capacity === "low" &&
                  "border-[var(--roma-terracotta)]/70 bg-[var(--roma-terracotta)]/8 hover:border-[var(--roma-terracotta)]/85 hover:bg-[var(--roma-terracotta)]/14",
                isSelected &&
                  "!border-[var(--roma-terracotta)] !bg-[var(--roma-saffron)] !text-[var(--roma-espresso)] shadow-sm ring-2 ring-[var(--roma-terracotta)]/35 hover:!bg-[var(--roma-saffron-deep)] hover:!text-[var(--roma-espresso)]",
              )}
            >
              {formatTime12h(slot.time)}
            </Button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pt-0.5 text-[11px] font-medium text-[var(--roma-espresso)]/72 sm:gap-4 sm:text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[var(--roma-olive)]" /> Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-600" /> Filling up
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-[var(--roma-terracotta)]" /> Almost full
        </span>
      </div>
    </div>
  );
}
